import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M12_ROLES, hasAnyRole } from '../../lib/roles';
import type { ApplicantType, InvestmentProject, InvestmentSector, InvestorType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const SECTORS: InvestmentSector[] = [
  'TOURISM', 'FISHERIES', 'BLUE_ECONOMY', 'ICT', 'AGRICULTURE', 'MANUFACTURING',
  'FINANCIAL_SERVICES', 'REAL_ESTATE', 'RENEWABLE_ENERGY', 'EDUCATION', 'HEALTHCARE', 'CREATIVE_INDUSTRIES',
];

const TABS = [
  { key: 'apply', label: 'Apply' },
  { key: 'mine', label: 'My projects' },
  { key: 'all', label: 'All projects (staff)' },
] as const;

export function InvestmentProjects() {
  const { persona } = useAuth();
  const isFacilitator = hasAnyRole(persona?.roles, M12_ROLES.INVESTMENT_FACILITATOR);
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('apply');

  return (
    <div>
      <PageHeader title="Investment Projects" subtitle="The one-stop investor journey: mega-case orchestration, facilitator review, and incentive certification." />
      <Tabs tabs={isFacilitator ? TABS : TABS.filter((t) => t.key !== 'all')} value={tab} onChange={setTab} />
      {tab === 'apply' ? <ApplyForm /> : tab === 'mine' ? <MineProjects /> : <AllProjects />}
    </div>
  );
}

function ApplyForm() {
  const { persona } = useAuth();
  const options: Array<{ type: ApplicantType; id: string; label: string }> = [];
  if (persona?.nin) options.push({ type: 'PERSON', id: persona.nin, label: `Myself (${persona.nin})` });
  if (persona?.actingForBusinessId) options.push({ type: 'ENTITY', id: persona.actingForBusinessId, label: `My business (${persona.actingForBusinessId})` });

  // Hooks called unconditionally — see Permits.tsx's ApplyForm for why.
  const [applicantIdx, setApplicantIdx] = useState(0);
  const [investorType, setInvestorType] = useState<InvestorType>('LOCAL');
  const [investorName, setInvestorName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [sector, setSector] = useState<InvestmentSector>('TOURISM');
  const [description, setDescription] = useState('');
  const [capexAmount, setCapexAmount] = useState('');
  const [capexCurrency, setCapexCurrency] = useState<'SCR' | 'USD'>('USD');
  const [localJobs, setLocalJobs] = useState('0');
  const [foreignJobs, setForeignJobs] = useState('0');
  const [district, setDistrict] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<InvestmentProject | null>(null);

  if (!persona) return <InfoNote message="Sign in to submit an investment project." />;
  if (options.length === 0) return <InfoNote message="Your account has no NIN or business mandate on file — sign in as a trader/investor persona." />;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const applicant = options[applicantIdx]!;
      const res = await api.post<InvestmentProject>(
        '/api/v1/m12/investment-projects',
        {
          applicantType: applicant.type,
          applicantId: applicant.id,
          investorType,
          investorName,
          contactEmail,
          contactPhone: contactPhone || undefined,
          sector,
          description,
          capexAmount: Number.parseFloat(capexAmount),
          capexCurrency,
          employmentPlanLocalJobs: Number.parseInt(localJobs, 10),
          employmentPlanForeignJobs: Number.parseInt(foreignJobs, 10),
          district: district || undefined,
        },
        { actingFor: applicant.type === 'ENTITY' ? applicant.id : undefined, idempotencyKey: newIdempotencyKey() },
      );
      setCreated(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Submission failed');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="card border-emerald-200 bg-emerald-50 p-5">
        <h2 className="text-sm font-bold text-emerald-800">Project {created.refNo} submitted</h2>
        <p className="mt-1 text-xs text-emerald-700">
          State: {created.state}{created.megaCaseRef ? ` · FlowEngine mega-case ${created.megaCaseRef} opened.` : ' · FlowEngine mega-case not opened (best-effort — see project dashboard).'}
        </p>
        <Link to={`/trade/investment-projects/${created.refNo}`} className="btn-primary mt-3 inline-flex !bg-emerald-600 hover:!bg-emerald-700">View dashboard</Link>
      </div>
    );
  }

  return (
    <div className="card p-5">
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="space-y-3">
        {options.length > 1 ? (
          <Field label="Applying as">
            <select className="field-input" value={applicantIdx} onChange={(e) => setApplicantIdx(Number(e.target.value))}>
              {options.map((o, i) => <option key={o.id} value={i}>{o.label}</option>)}
            </select>
          </Field>
        ) : null}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Investor type">
            <select className="field-input" value={investorType} onChange={(e) => setInvestorType(e.target.value as InvestorType)}>
              <option value="LOCAL">Local</option><option value="FOREIGN">Foreign</option>
            </select>
          </Field>
          <Field label="Investor / company name" className="col-span-2"><input className="field-input" value={investorName} onChange={(e) => setInvestorName(e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact email"><input className="field-input" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></Field>
          <Field label="Contact phone (optional)"><input className="field-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} /></Field>
        </div>
        <Field label="Sector">
          <select className="field-input" value={sector} onChange={(e) => setSector(e.target.value as InvestmentSector)}>
            {SECTORS.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Project description"><textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <div className="grid grid-cols-4 gap-3">
          <Field label="Capex amount"><input className="field-input" type="number" min="0" value={capexAmount} onChange={(e) => setCapexAmount(e.target.value)} /></Field>
          <Field label="Currency">
            <select className="field-input" value={capexCurrency} onChange={(e) => setCapexCurrency(e.target.value as typeof capexCurrency)}>
              <option value="USD">USD</option><option value="SCR">SCR</option>
            </select>
          </Field>
          <Field label="Local jobs"><input className="field-input" type="number" min="0" value={localJobs} onChange={(e) => setLocalJobs(e.target.value)} /></Field>
          <Field label="Foreign jobs"><input className="field-input" type="number" min="0" value={foreignJobs} onChange={(e) => setForeignJobs(e.target.value)} /></Field>
        </div>
        <Field label="District (optional)"><input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. Anse Royale" /></Field>
        <button className="btn-primary" onClick={submit} disabled={busy || !investorName || !contactEmail || !description || !capexAmount}>{busy ? 'Submitting…' : 'Submit project'}</button>
      </div>
    </div>
  );
}

function MineProjects() {
  const { persona } = useAuth();
  const mineQ = useApiQuery<{ items: InvestmentProject[] }>('/api/v1/m12/investment-projects/me', persona?.actingForBusinessId);
  return (
    <div>
      {mineQ.error ? <ErrorNote message={mineQ.error} /> : null}
      {mineQ.loading ? <Spinner /> : (mineQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No projects yet" /> : (
        <div className="space-y-2">
          {mineQ.data!.items.map((p) => (
            <Link key={p.id} to={`/trade/investment-projects/${p.refNo}`} className="card block p-4 hover:border-brand-300">
              <div className="flex items-center justify-between"><span className="font-mono text-sm font-semibold">{p.refNo}</span><StatusBadge status={p.state} /></div>
              <div className="mt-1 text-xs text-slate-500">{p.sector.replaceAll('_', ' ')} · {p.investorName} · {scr(p.capexAmount)} {p.capexCurrency}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AllProjects() {
  const [status, setStatus] = useState('');
  const [sector, setSector] = useState('');
  const [q, setQ] = useState('');
  const query = new URLSearchParams({ limit: '50' });
  if (status) query.set('status', status);
  if (sector) query.set('sector', sector);
  if (q) query.set('q', q);
  const listQ = useApiQuery<Page<InvestmentProject>>(`/api/v1/m12/investment-projects?${query.toString()}`);

  return (
    <div>
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'IN_DELIVERY', 'OPERATIONAL', 'REJECTED', 'WITHDRAWN'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Sector" className="!mb-0">
          <select className="field-input" value={sector} onChange={(e) => setSector(e.target.value)}>
            <option value="">All sectors</option>
            {SECTORS.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Search" className="!mb-0"><input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ref no, investor name…" /></Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? <Spinner /> : (listQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No projects match" /> : (
          <table className="w-full">
            <thead><tr><th className="table-head">Ref</th><th className="table-head">Investor</th><th className="table-head">Sector</th><th className="table-head text-right">Capex</th><th className="table-head">Status</th></tr></thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs"><Link className="text-brand-600 hover:underline" to={`/trade/investment-projects/${p.refNo}`}>{p.refNo}</Link></td>
                  <td className="table-cell">{p.investorName}</td>
                  <td className="table-cell text-xs">{p.sector.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-right tabular-nums">{scr(p.capexAmount)} {p.capexCurrency}</td>
                  <td className="table-cell"><StatusBadge status={p.state} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
